import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Wrench, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminMaintenancePage = () => {
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await adminAPI.getSettings();
        const site = res.data.site_settings || {};
        setMaintenance({ enabled: site.maintenance_enabled || false, message: site.maintenance_message || '' });
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSiteSettings({ maintenance_enabled: maintenance.enabled, maintenance_message: maintenance.message });
      toast.success('Maintenance settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Maintenance Mode</h1>
        <p className="text-[#5F6368]">Show a maintenance page to all users when enabled</p>
      </div>

      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FEF7E0] rounded-xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-[#F9AB00]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#202124]">Maintenance Mode</h3>
              <p className="text-sm text-[#5F6368]">{maintenance.enabled ? 'Site is in maintenance' : 'Site is live'}</p>
            </div>
          </div>
          <button onClick={() => setMaintenance({ ...maintenance, enabled: !maintenance.enabled })}
            className={`w-14 h-7 rounded-full transition-colors ${maintenance.enabled ? 'bg-[#F9AB00]' : 'bg-[#DADCE0]'}`}
            data-testid="maintenance-toggle">
            <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${maintenance.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Maintenance Message</label>
            <textarea value={maintenance.message} onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
              className="input-google min-h-[120px]" placeholder="We are currently performing scheduled maintenance. Please check back shortly..."
              data-testid="maintenance-message" />
          </div>

          {maintenance.enabled && (
            <div className="p-4 bg-[#FEF7E0] border border-[#F9AB00] rounded-lg">
              <p className="text-sm font-medium text-[#202124]">Preview:</p>
              <p className="text-sm text-[#5F6368] mt-1">{maintenance.message || 'We are currently performing maintenance. Please check back shortly.'}</p>
            </div>
          )}

          <button onClick={handleSave} disabled={saving} className="btn-google flex items-center gap-2" data-testid="save-maintenance-btn">
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMaintenancePage;
